import "katex/dist/katex.css";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $wrapNodeInElement } from "@lexical/utils";
import {
  $createParagraphNode,
  $insertNodes,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_EDITOR,
  LexicalEditor,
} from "lexical";
import { useCallback, useEffect } from "react";

import { $createEquationNode, EquationNode } from "../nodes/EquationNode";
import { INSERT_EQUATION_COMMAND, EquationCommandPayload } from "../commands/equationCommands";
import { KatexEquationAlterer } from "@/features/notes/components/Katex/KatexEquationAlterer";

interface InsertEquationDialogProps {
  activeEditor: LexicalEditor;
  onClose: () => void;
}

export function InsertEquationDialog({ activeEditor, onClose }: InsertEquationDialogProps) {
  const onEquationConfirm = useCallback(
    (equation: string, inline: boolean) => {
      if (equation.trim() === "") {
        onClose();
        return;
      }

      activeEditor.dispatchCommand(INSERT_EQUATION_COMMAND, {
        equation,
        inline,
      });
      onClose();
    },
    [activeEditor, onClose]
  );

  return <KatexEquationAlterer onConfirm={onEquationConfirm} />;
}

export function EquationsPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([EquationNode])) {
      throw new Error("EquationsPlugin: EquationNode not registered on editor");
    }

    return editor.registerCommand<EquationCommandPayload>(
      INSERT_EQUATION_COMMAND,
      (payload) => {
        const { equation, inline } = payload;
        const equationNode = $createEquationNode(equation, inline);

        $insertNodes([equationNode]);
        if ($isRootOrShadowRoot(equationNode.getParentOrThrow())) {
          $wrapNodeInElement(equationNode, $createParagraphNode).selectEnd();
        }

        return true;
      },
      COMMAND_PRIORITY_EDITOR
    );
  }, [editor]);

  return null;
}
